import React, { useState, useMemo } from "react";
import { useQuery, useAction } from "wasp/client/operations";
import { getServices, deleteService } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { DashboardLayout } from "../../client/components/Sidebar";
import { useLocationContext } from "../../client/contexts/LocationContext";
import { useRequireSectionVisible } from "../../client/hooks/useRequireSectionVisible";
import {
  EnhancedActionButton,
  EnhancedIconButton,
  enhancedPageLayoutStyles,
  useResponsiveLayout,
} from "../../components/ui/enhanced-page-layout";
import {
  Search,
  Filter,
  Plus,
  Eye,
  Edit,
  Trash2,
  DollarSign,
  Tag,
  Phone,
  Clock,
  Star,
  Award,
  Grid3X3,
  List,
  Table,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import { cn } from "../../lib/utils";

export default function ServicesPage() {
  useRequireSectionVisible("showService");
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const deleteServiceFn = useAction(deleteService);

  // State for filters and view mode
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>(
    searchParams.get("category") || "all",
  );
  const [selectedListingType, setSelectedListingType] = useState<string>("all");
  const [selectedPriceType, setSelectedPriceType] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "title" | "price">(
    "newest",
  );

  // Use responsive layout hook
  const {
    viewMode,
    setViewMode,
    isFilterExpanded,
    setIsFilterExpanded,
    isMobile,
    shouldCollapseSidebar,
    hideViewSwitcher,
  } = useResponsiveLayout("table");

  // ... (keeping fetch logic same)

  const {
    data: services = [],
    isLoading,
    error,
  } = useQuery(getServices, {
    locationId: selectedLocation?.id || "",
    filters: {
      category: selectedCategory === "all" ? undefined : selectedCategory,
      listingType:
        selectedListingType === "all"
          ? undefined
          : (selectedListingType as "OFFER" | "REQUEST"),
      priceType: selectedPriceType === "all" ? undefined : selectedPriceType,
    },
  });

  // ... (keeping sort logic same)

  const filteredServices = useMemo(() => {
    let filtered = services as any[];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (service: any) =>
          service.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          service.description
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          service.metadata?.experience
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()),
      );
    }

    // Sort
    switch (sortBy) {
      case "newest":
        filtered = [...filtered].sort(
          (a: any, b: any) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        break;
      case "oldest":
        filtered = [...filtered].sort(
          (a: any, b: any) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
        break;
      case "title":
        filtered = [...filtered].sort((a: any, b: any) =>
          a.title.localeCompare(b.title),
        );
        break;
      case "price":
        filtered = [...filtered].sort(
          (a: any, b: any) =>
            (b.metadata?.price || 0) - (a.metadata?.price || 0),
        );
        break;
    }

    return filtered;
  }, [
    services,
    searchTerm,
    selectedCategory,
    selectedListingType,
    selectedPriceType,
    sortBy,
  ]);

  const handleDelete = async (serviceId: string) => {
    if (window.confirm("Are you sure you want to delete this service?")) {
      try {
        await deleteServiceFn({ itemId: serviceId });
        // The query will automatically refetch due to Wasp's reactivity
      } catch (error) {
        console.error("Error deleting service:", error);
        alert("Error deleting service. Please try again.");
      }
    }
  };

  // Authentication check
  if (!user) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Authentication Required
            </h2>
            <p className="text-gray-600">
              You must be logged in to view services.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const getCategoryColor = (category: string) => {
    const colors = {
      TUTORING: "bg-blue-100 text-blue-800",
      CLEANING: "bg-green-100 text-green-800",
      REPAIR: "bg-orange-100 text-orange-800",
      PERSONAL_CARE: "bg-pink-100 text-pink-800",
      TRANSPORTATION: "bg-purple-100 text-purple-800",
      EVENT_PLANNING: "bg-yellow-100 text-yellow-800",
      PHOTOGRAPHY: "bg-red-100 text-red-800",
      CONSULTING: "bg-indigo-100 text-indigo-800",
      OTHER: "bg-gray-100 text-gray-800",
    };
    return (
      colors[category as keyof typeof colors] || "bg-gray-100 text-gray-800"
    );
  };

  const getPriceTypeColor = (priceType: string) => {
    const colors = {
      HOURLY: "bg-blue-100 text-blue-800",
      FIXED: "bg-green-100 text-green-800",
      PER_SESSION: "bg-purple-100 text-purple-800",
      FREE: "bg-gray-100 text-gray-800",
    };
    return (
      colors[priceType as keyof typeof colors] || "bg-gray-100 text-gray-800"
    );
  };

  const getListingTypeColor = (type: string) => {
    return type === "OFFER"
      ? "bg-emerald-100 text-emerald-800"
      : "bg-amber-100 text-amber-800";
  };

  return (
    <DashboardLayout
      forceSidebarCollapse={shouldCollapseSidebar}
      showViewSwitcher={!hideViewSwitcher}
      activeView={viewMode}
      onViewChange={setViewMode}
    >
      <div className="w-full h-full flex flex-col">
        {/* Fixed Main Header */}
        <div className="fixed top-[var(--header-height)] left-0 lg:left-[var(--sidebar-width)] right-0 z-20 bg-white border-b border-gray-200 transition-all duration-300">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Services</h1>
                <p className="text-sm text-gray-600 mt-1">
                  Discover services and service requests at{" "}
                  {selectedLocation?.name} ({filteredServices.length} items)
                </p>
              </div>
              <div className="flex items-center gap-3">
                <EnhancedActionButton
                  onClick={() => setIsFilterExpanded(!isFilterExpanded)}
                  icon={Filter}
                  label=""
                  variant={isFilterExpanded ? "primary" : "secondary"}
                  className="hidden lg:flex"
                />

                {/* Mobile Filter Button */}
                <Sheet open={isFilterExpanded && isMobile} onOpenChange={setIsFilterExpanded}>
                  <EnhancedActionButton
                    onClick={() => setIsFilterExpanded(true)}
                    icon={Filter}
                    label=""
                    variant={isFilterExpanded ? "primary" : "secondary"}
                    className="lg:hidden"
                  />
                  <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                    <SheetHeader>
                      <SheetTitle>Filters</SheetTitle>
                    </SheetHeader>
                    <div className="py-6 space-y-6">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Search</label>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <input
                            type="text"
                            placeholder="Search services..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium">Category</label>
                        <select
                          value={selectedCategory}
                          onChange={(e) => setSelectedCategory(e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="all">All Categories</option>
                          <option value="TUTORING">Tutoring</option>
                          <option value="CLEANING">Cleaning</option>
                          <option value="REPAIR">Repair</option>
                          <option value="PERSONAL_CARE">Personal Care</option>
                          <option value="TRANSPORTATION">Transportation</option>
                          <option value="EVENT_PLANNING">Event Planning</option>
                          <option value="PHOTOGRAPHY">Photography</option>
                          <option value="CONSULTING">Consulting</option>
                          <option value="OTHER">Other</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium">Type</label>
                        <select
                          value={selectedListingType}
                          onChange={(e) => setSelectedListingType(e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="all">All Types</option>
                          <option value="OFFER">Offers</option>
                          <option value="REQUEST">Requests</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium">Sort By</label>
                        <select
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value as any)}
                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="newest">Newest First</option>
                          <option value="oldest">Oldest First</option>
                          <option value="title">Title A-Z</option>
                          <option value="price">Price (High-Low)</option>
                        </select>
                      </div>

                      <Button 
                        className="w-full" 
                        onClick={() => setIsFilterExpanded(false)}
                      >
                        Apply Filters
                      </Button>
                    </div>
                  </SheetContent>
                </Sheet>
                {user?.isAdmin && (
                  <EnhancedActionButton
                    onClick={() => {
                      if (selectedLocation?.locationCode) {
                        navigate(
                          `/locations/${selectedLocation.locationCode}/services/add`,
                        );
                      } else {
                        navigate("/services/add");
                      }
                    }}
                    icon={Plus}
                    label="Offer/Request Service"
                    hideLabelOnMobile
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Fixed Table Header (Conditional - Desktop only) */}
        {viewMode === "table" && (
          <div 
            className={cn(
              "fixed top-[calc(var(--header-height)+5rem)] left-0 lg:left-[var(--sidebar-width)] right-0 z-10 bg-sidebar-hover border-b border-border transition-all duration-300 hidden lg:block",
              isFilterExpanded ? "max-h-[500px]" : "max-h-[45px] overflow-hidden"
            )}
          >
            <div className="px-6 py-3 space-y-3">
              {/* Column Headers */}
              <div className="grid grid-cols-12 gap-4 text-xs font-medium text-content-text-muted uppercase tracking-wider select-none cursor-default">
                 <div className="col-span-3">Service</div>
                 <div className="col-span-1">Type</div>
                 <div className="col-span-2">Category</div>
                 <div className="col-span-1">Price Type</div>
                 <div className="col-span-1">Price</div>
                 <div className={user?.isAdmin ? "col-span-2" : "col-span-3"}>Availability</div>
                 <div className="col-span-1">Provider</div>
                 {user?.isAdmin && <div className="col-span-1 text-right">Actions</div>}
               </div>

              {/* Column Filters */}
              <div
                className={cn(
                  "grid grid-cols-12 gap-4 transition-all duration-300 ease-in-out",
                  isFilterExpanded
                    ? "max-h-[500px] opacity-100"
                    : "max-h-0 opacity-0 overflow-hidden",
                )}
              >
                <div className="col-span-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search services..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div className="col-span-1">
                  <select
                    value={selectedListingType}
                    onChange={(e) => setSelectedListingType(e.target.value)}
                    className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All</option>
                    <option value="OFFER">Offer</option>
                    <option value="REQUEST">Request</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All</option>
                    <option value="TUTORING">Tutoring</option>
                    <option value="CLEANING">Cleaning</option>
                    <option value="REPAIR">Repair</option>
                    <option value="PERSONAL_CARE">Personal Care</option>
                    <option value="TRANSPORTATION">Transportation</option>
                    <option value="EVENT_PLANNING">Event Planning</option>
                    <option value="PHOTOGRAPHY">Photography</option>
                    <option value="CONSULTING">Consulting</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
                <div className="col-span-1">
                  <select
                    value={selectedPriceType}
                    onChange={(e) => setSelectedPriceType(e.target.value)}
                    className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All</option>
                    <option value="HOURLY">Hourly</option>
                    <option value="FIXED">Fixed</option>
                    <option value="PER_SESSION">Per Session</option>
                    <option value="FREE">Free</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <select
                    value={sortBy === "price" ? "price" : "all"}
                    onChange={(e) =>
                      setSortBy(e.target.value === "price" ? "price" : "newest")
                    }
                    className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All</option>
                    <option value="price">High-Low</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <select className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                    <option value="all">All</option>
                    <option value="AVAILABLE">Available</option>
                    <option value="LIMITED">Limited</option>
                    <option value="BUSY">Busy</option>
                  </select>
                </div>
                <div className="col-span-1">
                  <select
                    value={
                      sortBy === "newest" ||
                      sortBy === "oldest" ||
                      sortBy === "title"
                        ? sortBy
                        : "newest"
                    }
                    onChange={(e) => setSortBy(e.target.value as any)}
                    className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="newest">Newest</option>
                    <option value="oldest">Oldest</option>
                    <option value="title">A-Z</option>
                  </select>
                </div>
                <div className="col-span-1">
                  {/* Actions column - no filter needed */}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Fixed Filters (Desktop) - only for cards/list view */}
        {!isLoading && !error && services.length > 0 && viewMode !== "table" && (
          <div
            className={cn(
              "fixed top-[calc(var(--header-height)+5rem)] left-0 lg:left-[var(--sidebar-width)] right-0 z-10 bg-white border-b border-gray-200 transition-all duration-300 ease-in-out hidden lg:block",
              isFilterExpanded
                ? "translate-y-0 opacity-100"
                : "-translate-y-full opacity-0 pointer-events-none",
            )}
          >
            <div className="px-4 py-3">
              <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search services..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Categories</option>
                  <option value="TUTORING">Tutoring</option>
                  <option value="CLEANING">Cleaning</option>
                  <option value="REPAIR">Repair</option>
                  <option value="PERSONAL_CARE">Personal Care</option>
                  <option value="TRANSPORTATION">Transportation</option>
                  <option value="EVENT_PLANNING">Event Planning</option>
                  <option value="PHOTOGRAPHY">Photography</option>
                  <option value="CONSULTING">Consulting</option>
                  <option value="OTHER">Other</option>
                </select>

                <select
                  value={selectedListingType}
                  onChange={(e) => setSelectedListingType(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Types</option>
                  <option value="OFFER">Offers</option>
                  <option value="REQUEST">Requests</option>
                </select>

                <select
                  value={selectedPriceType}
                  onChange={(e) => setSelectedPriceType(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Price Types</option>
                  <option value="HOURLY">Hourly</option>
                  <option value="FIXED">Fixed</option>
                  <option value="PER_SESSION">Per Session</option>
                  <option value="FREE">Free</option>
                </select>

                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="title">Title A-Z</option>
                  <option value="price">Price High-Low</option>
                </select>
              </div>
            </div>
          </div>
        )}

        <div
          className={cn(
            "flex-1 overflow-y-auto transition-[padding] duration-300 ease-in-out",
            isFilterExpanded && !isMobile
              ? (viewMode === "table" ? "pt-[calc(var(--header-height)+11rem)]" : "pt-[calc(var(--header-height)+8.5rem)]")
              : "pt-[calc(var(--header-height)+5.5rem)]"
          )}
        >
          <div className="w-full">
            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-600 mt-2">Loading services...</p>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Award className="w-12 h-12 text-red-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Error Loading Services
                  </h3>
                  <p className="text-gray-600">Please try again later.</p>
                </div>
              </div>
            )}

            {/* Empty State */}
            {!isLoading && !error && filteredServices.length === 0 && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Award className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No Services Found
                  </h3>
                  <p className="text-gray-600">
                    {searchTerm ||
                    selectedCategory !== "all" ||
                    selectedPriceType !== "all"
                      ? "Try adjusting your search or filters to find what you're looking for."
                      : "No services have been offered in this location yet."}
                  </p>
                </div>
              </div>
            )}

            {/* Content */}
            {!isLoading && !error && filteredServices.length > 0 && (
              <>
                {/* Cards View - Use enhanced layout with padding */}
                {viewMode === "cards" && (
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {filteredServices.map((service) => (
                        <div
                          key={service.id}
                          className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                          onClick={() => navigate(`/services/${service.id}`)}
                        >
                          <div className="p-6">
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex items-center gap-2">
                                <span
                                  className={`px-2 py-1 text-xs font-medium rounded ${getCategoryColor(
                                    service.metadata?.category,
                                  )}`}
                                >
                                  {service.metadata?.category?.replace(
                                    "_",
                                    " ",
                                  )}
                                </span>
                                {service.metadata?.listingType && (
                                  <span
                                    className={`px-2 py-1 text-xs font-medium rounded ${getListingTypeColor(
                                      service.metadata.listingType,
                                    )}`}
                                  >
                                    {service.metadata.listingType === "REQUEST"
                                      ? "REQUEST"
                                      : "OFFER"}
                                  </span>
                                )}
                                <span
                                  className={`px-2 py-1 text-xs font-medium rounded ${getPriceTypeColor(
                                    service.metadata?.priceType,
                                  )}`}
                                >
                                  {service.metadata?.priceType?.replace(
                                    "_",
                                    " ",
                                  )}
                                </span>
                              </div>
                              {service.metadata?.price &&
                                service.metadata.priceType !== "FREE" && (
                                  <div className="text-lg font-bold text-green-600">
                                    ${service.metadata.price}
                                  </div>
                                )}
                              {service.metadata?.priceType === "FREE" && (
                                <div className="text-lg font-bold text-gray-600">
                                  Free
                                </div>
                              )}
                            </div>

                            <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                              {service.title}
                            </h3>

                            <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                              {service.description}
                            </p>

                            {service.metadata?.duration && (
                              <div className="flex items-center gap-1 text-sm text-gray-500 mb-2">
                                <Clock className="w-3 h-3" />
                                {service.metadata.duration}
                              </div>
                            )}

                            {service.metadata?.experience && (
                              <div className="flex items-center gap-1 text-sm text-gray-500 mb-4">
                                <Star className="w-3 h-3" />
                                {service.metadata.experience}
                              </div>
                            )}

                            <div className="flex items-center justify-between">
                              <div className="text-sm text-gray-500">
                                by {service.author.name || service.author.email}
                              </div>
                              <div
                                className="flex items-center gap-2"
                                onClick={(e) => e?.stopPropagation()}
                              >
                                <EnhancedIconButton
                                  onClick={() =>
                                    navigate(`/services/${service.id}`)
                                  }
                                  icon={Eye}
                                  title="View details"
                                />
                                {user?.isAdmin && (
                                  <>
                                    <EnhancedIconButton
                                      onClick={() =>
                                        navigate(`/services/${service.id}/edit`)
                                      }
                                      icon={Edit}
                                      title="Edit service"
                                    />
                                    <EnhancedIconButton
                                      onClick={() => handleDelete(service.id)}
                                      icon={Trash2}
                                      title="Delete service"
                                      variant="danger"
                                    />
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* List View - Use enhanced layout with padding */}
                {viewMode === "list" && (
                  <div className="p-6">
                    <div className="space-y-4">
                      {filteredServices.map((service) => (
                        <div
                          key={service.id}
                          className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer hover:bg-hover transition-colors"
                          onClick={() => navigate(`/services/${service.id}`)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="text-lg font-semibold text-gray-900">
                                  {service.title}
                                </h3>
                                <span
                                  className={`px-2 py-1 text-xs font-medium rounded ${getCategoryColor(
                                    service.metadata?.category,
                                  )}`}
                                >
                                  {service.metadata?.category?.replace(
                                    "_",
                                    " ",
                                  )}
                                </span>
                                {service.metadata?.listingType && (
                                  <span
                                    className={`px-2 py-1 text-xs font-medium rounded ${getListingTypeColor(
                                      service.metadata.listingType,
                                    )}`}
                                  >
                                    {service.metadata.listingType === "REQUEST"
                                      ? "REQUEST"
                                      : "OFFER"}
                                  </span>
                                )}
                                <span
                                  className={`px-2 py-1 text-xs font-medium rounded ${getPriceTypeColor(
                                    service.metadata?.priceType,
                                  )}`}
                                >
                                  {service.metadata?.priceType?.replace(
                                    "_",
                                    " ",
                                  )}
                                </span>
                              </div>

                              <p className="text-gray-600 mb-3 line-clamp-2">
                                {service.description}
                              </p>

                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                <span>
                                  by{" "}
                                  {service.author.name || service.author.email}
                                </span>
                                <span>
                                  {new Date(
                                    service.createdAt,
                                  ).toLocaleDateString()}
                                </span>
                                {service.metadata?.duration && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {service.metadata.duration}
                                  </span>
                                )}
                                {service.metadata?.contactInfo && (
                                  <span className="flex items-center gap-1">
                                    <Phone className="w-3 h-3" />
                                    {service.metadata.contactInfo}
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-4 ml-4">
                              {service.metadata?.price &&
                                service.metadata.priceType !== "FREE" && (
                                  <div className="text-xl font-bold text-green-600">
                                    ${service.metadata.price}
                                  </div>
                                )}
                              {service.metadata?.priceType === "FREE" && (
                                <div className="text-xl font-bold text-gray-600">
                                  Free
                                </div>
                              )}
                              <div
                                className="flex items-center gap-2"
                                onClick={(e) => e?.stopPropagation()}
                              >
                                <EnhancedIconButton
                                  onClick={() =>
                                    navigate(`/services/${service.id}`)
                                  }
                                  icon={Eye}
                                  title="View details"
                                />
                                {user && service.authorId === user.id && (
                                  <>
                                    <EnhancedIconButton
                                      onClick={() =>
                                        navigate(`/services/${service.id}/edit`)
                                      }
                                      icon={Edit}
                                      title="Edit service"
                                    />
                                    <EnhancedIconButton
                                      onClick={() => handleDelete(service.id)}
                                      icon={Trash2}
                                      title="Delete service"
                                      variant="danger"
                                    />
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Table View */}
                {viewMode === "table" && (
                  <div className="w-full">
                    {/* Table Body - No internal scroll */}
                    <div className="divide-y divide-gray-200">
                      {filteredServices.map((service) => (
                        <div
                          key={service.id}
                          className="px-6 py-3 hover:bg-hover transition-colors cursor-pointer"
                          onClick={() => navigate(`/services/${service.id}`)}
                        >
                          <div className="grid grid-cols-12 gap-4 items-start">
                            <div className="col-span-3">
                              <div className="text-sm font-medium text-gray-900">
                                {service.title}
                              </div>
                              <div className="text-sm text-gray-500 mt-1 line-clamp-2">
                                {service.description}
                              </div>
                            </div>

                            <div className="col-span-1">
                              <span
                                className={`text-xs font-medium px-2 py-1 rounded ${getListingTypeColor(
                                  service.metadata?.listingType,
                                )}`}
                              >
                                {service.metadata?.listingType === "REQUEST"
                                  ? "Req"
                                  : "Off"}
                              </span>
                            </div>
                            <div className="col-span-2">
                              <span className="text-sm text-gray-600">
                                {service.metadata?.category?.replace("_", " ")}
                              </span>
                            </div>
                            <div className="col-span-1">
                              <span className="text-sm text-gray-600">
                                {service.metadata?.priceType?.replace("_", " ")}
                              </span>
                            </div>
                            <div className="col-span-1">
                              <span className="text-sm text-gray-600">
                                {service.metadata?.priceType === "FREE"
                                  ? "Free"
                                  : service.metadata?.price
                                    ? `$${service.metadata.price}`
                                    : "-"}
                              </span>
                            </div>
                            <div className={user?.isAdmin ? "col-span-2" : "col-span-3"}>
                              <span className="text-sm text-gray-600">
                                Available
                              </span>
                            </div>
                            <div className="col-span-1">
                              <span className="text-sm text-gray-500">
                                {service.author.username ||
                                  service.author.name ||
                                  service.author.email}
                              </span>
                            </div>
                            {user?.isAdmin && (
                              <div className="col-span-1 flex items-center justify-end gap-2" onClick={(e) => e?.stopPropagation()}>
                                <EnhancedIconButton
                                  onClick={() =>
                                    navigate(`/services/${service.id}/edit`)
                                  }
                                  icon={Edit}
                                  title="Edit service"
                                />
                                <EnhancedIconButton
                                  onClick={() => handleDelete(service.id)}
                                  icon={Trash2}
                                  title="Delete service"
                                  variant="danger"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
